/**
 * Me tab — the user's profile surface.
 *
 * Intentionally a lightweight frame, not a destination. Recent
 * Interactions is just one section; future phases layer on patterns,
 * insights, and other self-oriented views without touching this
 * file's structure.
 *
 * Phase A: Recent Interactions groups by session, so a three-way
 * conversation renders as one card and opens the unified
 * SessionDetail view (not one-card-per-participant).
 */
import { useState } from "react";

import type { Contact } from "@/lib/data";
import type { UserProfile } from "@/onboarding/types";

import { RecentInteractionsSection } from "./RecentInteractionsSection";
import { SessionDetail } from "./SessionDetail";
import type { SessionInteractionGroup } from "./sessionInteractions";

interface MeProps {
  profile: UserProfile;
  contacts: Contact[];
}

export function Me({ profile, contacts }: MeProps) {
  const [selected, setSelected] = useState<SessionInteractionGroup | null>(null);

  return (
    <div className="flex flex-col gap-6 px-5 pb-28 pt-[calc(env(safe-area-inset-top,0px)+16px)]">
      <header className="flex flex-col gap-1">
        <h1 className="text-foreground text-[34px] font-bold tracking-tight">
          {profile.name || "Me"}
        </h1>
        <p className="text-muted-foreground text-[14px]">
          Your conversations, your patterns.
        </p>
      </header>

      <RecentInteractionsSection
        contacts={contacts}
        onSelect={(group) => setSelected(group)}
      />

      {selected && (
        <SessionDetail
          group={selected}
          contacts={contacts}
          onClose={() => setSelected(null)}
        />
      )}
    </div>
  );
}
